import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import JournalNavigation from '../../components/JournalNavigation';
import LockIndicator from '../../components/LockIndicator';
import JournalStartModal from '../../components/JournalStartModal';
import { getJournalStartConfig } from '../../utils/journalConstants';
import './SelfAssessment.css';

const SelfAssessment = () => {
    const [showStartModal, setShowStartModal] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [formData, setFormData] = useState({
        // Page 1: Expectations
        expectations: '',
        signature: '',
        date: new Date().toISOString().split('T')[0],
        // Page 2: Current Health - Vitals
        age: '',
        bloodPressure: '',
        weight: '',
        weightUnit: 'kg',
        height: '',
        heightUnit: 'cm',
        fastingGlucose: '',
        fastingGlucoseUnit: 'mmol/L',
        waistCircumference: '',
        waistCircumferenceUnit: 'cm',
        bodyAppreciation: '',
        // Page 3: Symptoms and Improvements
        symptomsAndImprovements: '',
        // Page 4: Symptom Rating
        symptom1: '', rating1: '',
        symptom2: '', rating2: '',
        symptom3: '', rating3: '',
        symptom4: '', rating4: '',
        symptom5: '', rating5: '',
        symptom6: '', rating6: '',
        symptom7: '', rating7: '',
        symptom8: '', rating8: '',
        symptom9: '', rating9: '',
        symptom10: '', rating10: '',
        selectedSymptoms: {}
    });
    const [saveStatus, setSaveStatus] = useState('');
    const [entryId, setEntryId] = useState(null);
    const [entryMetadata, setEntryMetadata] = useState({ lockedAt: null, startedAt: null, createdAt: null });

    // Predefined symptoms list for Page 4
    const symptomsList = [
        'insomnia', 'heartburn', 'joint pain', 'constipation',
        'sinus pressure', 'diarrhoea', 'swelling', 'excessive thirst',
        'palpitations', 'excessive hunger', 'abdominal pain', 'anxiety',
        'abdominal bloating', 'depression', 'abdominal gas', 'period pain/erectile dysfunction'
    ];

    // Load existing entry
    useEffect(() => {
        loadEntry();
    }, []);

    // Reload when cycle changes
    useEffect(() => {
        const handler = () => { loadEntry(); };
        window.addEventListener('cycle-changed', handler);
        return () => window.removeEventListener('cycle-changed', handler);
    }, []);

    const loadEntry = async () => {
        try {
            const response = await api.get('/journal', {
                params: { section: 'self-assessment' }
            });
            if (response.data.entries && response.data.entries.length > 0) {
                const entry = response.data.entries[0];
                setEntryId(entry._id);
                setFormData({ ...formData, ...entry.data });
                setEntryMetadata({
                    lockedAt: entry.lockedAt,
                    startedAt: entry.startedAt,
                    createdAt: entry.createdAt
                });
                setHasStarted(!!entry.startedAt);
            }
            // Don't show modal on load - wait for user interaction
        } catch (error) {
            console.error('Error loading entry:', error);
        }
    };

    // Auto-save with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (Object.values(formData).some(val => val)) {
                saveEntry();
            }
        }, 2000);
        return () => clearTimeout(timer);
    }, [formData]);

    const saveEntry = async (includeStartTime = false) => {
        try {
            setSaveStatus('saving');
            const payload = {
                section: 'self-assessment',
                data: formData
            };

            // Include startedAt when user clicks "Begin"
            if (includeStartTime) {
                payload.startedAt = new Date().toISOString();
            }

            if (entryId) {
                const response = await api.patch(`/journal/${entryId}`, payload);
                // Update metadata if startedAt was just set
                if (response.data.entry && response.data.entry.startedAt) {
                    setEntryMetadata(prev => ({
                        ...prev,
                        startedAt: response.data.entry.startedAt
                    }));
                }
            } else {
                const response = await api.post('/journal', payload);
                setEntryId(response.data.entry._id);
                setEntryMetadata({
                    lockedAt: response.data.entry.lockedAt,
                    startedAt: response.data.entry.startedAt,
                    createdAt: response.data.entry.createdAt
                });
            }
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000);
        } catch (error) {
            console.error('Error saving:', error);
            setSaveStatus('error');
        }
    };

    const handleChange = (field, value) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleBegin = () => {
        setShowStartModal(false);
        setHasStarted(true);
        // Save entry with startedAt timestamp
        saveEntry(true);
    };

    const handleNotNow = () => {
        setShowStartModal(false);
        // Stay on the same page - user can try again later
    };

    const isLocked = entryMetadata.lockedAt !== null;

    // Note: symptom selection removed - ratings are editable directly

    // Get modal configuration from constants
    const modalConfig = getJournalStartConfig('self-assessment');

    return (
        <div className="self-assessment-container">
            <JournalStartModal
                isOpen={showStartModal}
                onBegin={handleBegin}
                onCancel={handleNotNow}
                title={modalConfig.title}
                message={modalConfig.message}
                estimatedTime={modalConfig.estimatedTime}
            />

            {saveStatus && (
                <div className={`save-status ${saveStatus}`}>
                    {saveStatus === 'saving' && '💾 Saving...'}
                    {saveStatus === 'saved' && '✓ Saved successfully'}
                    {saveStatus === 'error' && '⚠ Save failed'}
                </div>
            )}

            <LockIndicator
                lockedAt={entryMetadata.lockedAt}
                startedAt={entryMetadata.startedAt}
            />

            <div className="journal-page-wrapper">
                {/* Page 1 (S1): Expectations */}
                <Page1 formData={formData} onChange={handleChange} isLocked={isLocked} />

                {/* Page 2 (S2): Current Health Status - Vitals */}
                <div style={{ marginTop: '80px' }}>
                    <Page2 formData={formData} onChange={handleChange} isLocked={isLocked} />
                </div>

                {/* Page 3 (S3): Symptoms and Improvements */}
                <div style={{ marginTop: '80px' }}>
                    <Page3 formData={formData} onChange={handleChange} isLocked={isLocked} />
                </div>

                {/* Page 4 (S4): Symptom Rating */}
                <div style={{ marginTop: '80px' }}>
                    <Page4 formData={formData} onChange={handleChange} symptomsList={symptomsList} isLocked={isLocked} />
                </div>
            </div>
        </div>
    );
};

// Page 1: Expectations
const Page1 = ({ formData, onChange, isLocked }) => {
    return (
        <>
            <div className="section-header">
                <h1 className="journal-title">Expectations</h1>
            </div>

            <p className="journal-prompt">
                Imagine yourself three months from now. The healthier version of you. The version of yourself who is
                still a work-in-progress but has definitely made strides in your well-being. Breathe in. Breathe out.
                Write a letter to yourself about the motivation behind your wellness journey. Why did you decide to
                improve your health? What are your hopes for your wellbeing?
            </p>

            <div className="handwritten-label">
                Dear me,
            </div>

            <div className="journal-lines">
                <div className="journal-line multi-line">
                    <textarea
                        value={formData.expectations}
                        onChange={(e) => onChange('expectations', e.target.value)}
                        placeholder="Write letter here..."
                        rows={15}
                        disabled={isLocked}
                    />
                </div>
            </div>

            <div className="date-signature">
                <div className="signature-field">
                    <div className="handwritten-label" style={{ marginBottom: '10px' }}>
                        With great love,
                    </div>
                    <input
                        type="text"
                        value={formData.signature}
                        onChange={(e) => onChange('signature', e.target.value)}
                        placeholder="Me"
                        disabled={isLocked}
                    />
                </div>

                <div className="date-field">
                    <div className="handwritten-label" style={{ marginBottom: '10px' }}>
                        Date
                    </div>
                    <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => onChange('date', e.target.value)}
                        disabled={isLocked}
                    />
                </div>
            </div>


        </>
    );
};

// Page 2 (S2): Current Health Status - Vitals
const Page2 = ({ formData, onChange, isLocked }) => {
    return (
        <>
            <div className="section-header">
                <h1 className="journal-title">Current health status</h1>
            </div>

            <p className="journal-prompt">
                Reminder: we love ourselves right now - enough to keep improving.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginTop: '40px' }}>
                <div>
                    <div className="journal-line" style={{ marginBottom: '30px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', color: '#2c2416' }}>
                            Age
                        </label>
                        <input
                            type="number"
                            value={formData.age}
                            onChange={(e) => onChange('age', e.target.value)}
                            placeholder=""
                            onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                            disabled={isLocked}
                        />
                    </div>

                    <div className="journal-line" style={{ marginBottom: '30px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', color: '#2c2416' }}>
                            Weight
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                                type="number"
                                step="0.1"
                                value={formData.weight}
                                onChange={(e) => onChange('weight', e.target.value)}
                                placeholder="0.0"
                                onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                style={{ flex: 1 }}
                                disabled={isLocked}
                            />
                            <select
                                value={formData.weightUnit}
                                onChange={(e) => onChange('weightUnit', e.target.value)}
                                className='dropdown-style'
                                // style={{
                                //     padding: '8px',
                                //     border: 'none',
                                //     // border: '1px solid #ccc',
                                //     // borderRadius: '4px',
                                //     fontSize: '14px',
                                //     minWidth: '80px',
                                //     backgroundColor: 'transparent'
                                // }}
                                disabled={isLocked}
                            >
                                <option value="kg">kg</option>
                                <option value="lbs">lbs</option>
                            </select>
                        </div>
                    </div>

                    <div className="journal-line" style={{ marginBottom: '30px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', color: '#2c2416' }}>
                            Fasting blood glucose
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.fastingGlucose}
                                onChange={(e) => onChange('fastingGlucose', e.target.value)}
                                placeholder="0.00"
                                onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                style={{ flex: 1 }}
                                disabled={isLocked}
                            />
                            <select
                                value={formData.fastingGlucoseUnit}
                                onChange={(e) => onChange('fastingGlucoseUnit', e.target.value)}
                                className='dropdown-style'
                                // style={{
                                //     padding: '8px',
                                //     border: '1px solid #ccc',
                                //     borderRadius: '4px',
                                //     fontSize: '14px',
                                //     minWidth: '80px'
                                // }}
                                disabled={isLocked}
                            >
                                <option value="mmol/L">mmol/L</option>
                                <option value="mg/dL">mg/dL</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div>
                    <div className="journal-line" style={{ marginBottom: '30px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', color: '#2c2416' }}>
                            Blood Pressure (mmHg)
                        </label>
                        <input
                            type="text"
                            value={formData.bloodPressure}
                            onChange={(e) => onChange('bloodPressure', e.target.value)}
                            placeholder="120.5/80.0"
                            disabled={isLocked}
                        />
                    </div>

                    <div className="journal-line" style={{ marginBottom: '30px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', color: '#2c2416' }}>
                            Height
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                                type="number"
                                step="0.1"
                                value={formData.height}
                                onChange={(e) => onChange('height', e.target.value)}
                                placeholder="170"
                                onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                style={{ flex: 1 }}
                                disabled={isLocked}
                            />
                            <select
                                value={formData.heightUnit}
                                onChange={(e) => onChange('heightUnit', e.target.value)}
                                className='dropdown-style'
                                // style={{
                                //     padding: '8px',
                                //     border: '1px solid #ccc',
                                //     borderRadius: '4px',
                                //     fontSize: '14px',
                                //     minWidth: '80px'
                                // }}
                                disabled={isLocked}
                            >
                                <option value="cm">cm</option>
                                <option value="ft">ft</option>
                            </select>
                        </div>
                    </div>

                    <div className="journal-line" style={{ marginBottom: '30px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', color: '#2c2416' }}>
                            Waist circumference
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.waistCircumference}
                                onChange={(e) => onChange('waistCircumference', e.target.value)}
                                placeholder="0.00"
                                onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                style={{ flex: 1 }}
                                disabled={isLocked}
                            />
                            <select
                                value={formData.waistCircumferenceUnit}
                                onChange={(e) => onChange('waistCircumferenceUnit', e.target.value)}
                                className='dropdown-style'
                                // style={{
                                //     padding: '8px',
                                //     border: '1px solid #ccc',
                                //     borderRadius: '4px',
                                //     fontSize: '14px',
                                //     minWidth: '80px'
                                // }}
                                disabled={isLocked}
                            >
                                <option value="cm">cm</option>
                                <option value="in">inches</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div >

            <div style={{ marginTop: '50px' }}>
                <p className="journal-prompt">
                    List everything that you appreciate about your body and mind.
                </p>
                <div className="journal-line multi-line" style={{ marginTop: '20px' }}>
                    <textarea
                        value={formData.bodyAppreciation}
                        onChange={(e) => onChange('bodyAppreciation', e.target.value)}
                        placeholder=""
                        rows={12}
                        disabled={isLocked}
                    />
                </div>
            </div>
        </>
    );
};

// Page 3 (S3): Symptoms and Improvements
const Page3 = ({ formData, onChange, isLocked }) => {
    const handleTextSelection = () => {
        const selectedText = window.getSelection().toString().trim();

        if (selectedText && selectedText.length > 1) {
            // Find the next available symptom slot (1-10)
            for (let i = 1; i <= 10; i++) {
                if (!formData[`symptom${i}`]) {
                    onChange(`symptom${i}`, selectedText);

                    // Update the textarea content with bold formatting markers
                    const currentText = formData.symptomsAndImprovements;

                    // Check if word is already marked
                    if (!currentText.includes(`**${selectedText}**`)) {
                        // Word not marked, mark it
                        const regex = new RegExp(`\\b${selectedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
                        const updatedText = currentText.replace(regex, `**${selectedText}**`);
                        onChange('symptomsAndImprovements', updatedText);
                    }

                    // Show user feedback
                    const notification = document.createElement('div');
                    notification.innerHTML = `✓ "${selectedText}" highlighted and added to symptom ${i}`;
                    notification.style.cssText = `
                        position: fixed;
                        top: 20px;
                        right: 20px;
                        background: #4CAF50;
                        color: white;
                        padding: 12px 20px;
                        border-radius: 6px;
                        z-index: 1000;
                        font-size: 14px;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                        font-weight: 500;
                    `;
                    document.body.appendChild(notification);

                    setTimeout(() => {
                        if (document.body.contains(notification)) {
                            document.body.removeChild(notification);
                        }
                    }, 3000);

                    break;
                }
            }

            // Clear selection
            window.getSelection().removeAllRanges();
        }
    };

    // Function to render text with bold formatting for display
    const renderFormattedText = (text) => {
        if (!text) return '';
        // Replace **word** with bold HTML for display using CSS class
        return text.replace(/\*\*(.*?)\*\*/g, '<span class="highlighted-text">$1</span>');
    };

    return (
        <>
            <div className="section-header">
                <h1 className="journal-title">Current health status</h1>
            </div>

            <p className="journal-prompt">
                Write down all the symptoms that you are experiencing. List all the aspects that you would like to see
                improvements in your mind and body.
            </p>

            <div className="journal-line multi-line" style={{ marginTop: '30px', position: 'relative' }}>
                {/* Overlay div for displaying bold formatted text */}
                <div
                    id="overlay-div"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        padding: '8px 0',
                        border: 'none',
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        lineHeight: '32px',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        pointerEvents: 'none',
                        zIndex: 1,
                        overflow: 'auto',
                        background: 'transparent',
                        paddingTop: 0
                    }}
                    dangerouslySetInnerHTML={{
                        __html: renderFormattedText(formData.symptomsAndImprovements)
                    }}
                />

                {/* Actual textarea with original styling */}
                <textarea
                    id="symptoms-textarea"
                    value={formData.symptomsAndImprovements}
                    onChange={(e) => {
                        onChange('symptomsAndImprovements', e.target.value);
                        // Sync scroll position
                        const textarea = e.target;
                        const overlay = document.getElementById('overlay-div');
                        if (overlay) {
                            overlay.scrollTop = textarea.scrollTop;
                        }
                    }}
                    onScroll={(e) => {
                        // Sync scroll position
                        const textarea = e.target;
                        const overlay = document.getElementById('overlay-div');
                        if (overlay) {
                            overlay.scrollTop = textarea.scrollTop;
                        }
                    }}
                    onMouseUp={handleTextSelection}
                    onKeyUp={handleTextSelection}
                    placeholder="Start typing symptoms and health concerns here..."
                    rows={15}
                    style={{
                        position: 'relative',
                        zIndex: 2,
                        backgroundColor: 'transparent',
                        color: 'transparent',
                        caretColor: '#2c2416', // Show cursor
                        userSelect: 'text',
                        cursor: 'text',
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        lineHeight: '32px',
                        resize: 'vertical',
                        border: 'none',
                        outline: 'none',
                        paddingTop: 0
                    }}
                    disabled={isLocked}
                />
            </div>

            {/* <div style={{
                background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
                padding: '16px',
                borderRadius: '8px',
                marginTop: '20px',
                border: '1px solid #c8e6c9',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <p style={{
                    fontSize: '14px',
                    color: '#2e7d32',
                    margin: '0 0 8px 0',
                    fontWeight: '600'
                }}>
                    �️ How to highlight symptoms:
                </p>
                <p style={{
                    fontSize: '13px',
                    color: '#388e3c',
                    margin: '0',
                    lineHeight: '1.5'
                }}>
                    1. Select any word or phrase by highlighting it<br />
                    2. Selected words will appear in <span className="highlighted-text">bold blue</span> to show they're marked<br />
                    3. They'll automatically populate the rating fields below
                </p>
            </div> */}

            {/* <p className="journal-prompt" style={{ marginTop: '30px' }}>
                Words in <span className="highlighted-text">bold blue</span> have been selected and are ready for rating in the next section.
            </p> */}
        </>
    );
};

// Page 4 (S4): Symptom Rating
const Page4 = ({ formData, onChange, symptomsList, isLocked }) => {
    return (
        <>
            <p className="journal-prompt">
                List the symptoms /keywords that you have highlighted and give them a rating out of 10.
            </p>

            <div style={{
                background: '#f0f8ff',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #b0d4f1'
            }}>
                <p style={{
                    fontSize: '13px',
                    color: '#2c5aa0',
                    margin: '0',
                    lineHeight: '1.5'
                }}>
                    💡 <strong>Tip:</strong> Go back to the text area above and select/highlight any words or phrases.
                    They will automatically appear in the numbered fields below for rating.
                </p>
            </div>

            <p style={{ fontSize: '14px', color: '#3d3425', marginTop: '20px', lineHeight: '1.6' }}>
                <strong>1</strong> = not severe, intense or frequent (noticeable but very manageable)<br />
                <strong>10</strong> = very severe, intense or frequent (greatly affects daily life)
            </p>

            {/* Symptom Rating Grid */}
            <div className="symptom-rating-container">
                {[1, 3, 5, 7, 9].map((num) => (
                    <div key={num} className="symptom-rating-row">
                        {/* Left Column */}
                        <div className="symptom-rating-item">
                            <strong className="symptom-number">{num}.</strong>
                            <div className="journal-line" style={{ flex: 1, marginBottom: 0, position: 'relative' }}>
                                <input
                                    type="text"
                                    value={formData[`symptom${num}`]}
                                    onChange={(e) => onChange(`symptom${num}`, e.target.value)}
                                    placeholder=""
                                    style={{
                                        backgroundColor: 'transparent',
                                        paddingRight: formData[`symptom${num}`] ? '30px' : '10px'
                                    }}
                                    disabled={isLocked}
                                />
                                {formData[`symptom${num}`] && (
                                    <button
                                        type="button"
                                        onClick={() => onChange(`symptom${num}`, '')}
                                        style={{
                                            position: 'absolute',
                                            right: '8px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            color: '#999',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            lineHeight: '1'
                                        }}
                                        title="Clear this symptom"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            <div className="rating-input-wrapper">
                                <select
                                    value={formData[`rating${num}`]}
                                    onChange={(e) => onChange(`rating${num}`, e.target.value)}
                                    className="rating-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '24.5px',
                                        background: 'transparent',
                                        border: 'none',
                                        borderBottom: '1px solid #d4c4a8',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                                <span className="rating-divider" style={{
                                    fontFamily: 'Vintage Moon',
                                    fontSize: '24.5px'
                                }}>/10</span>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="symptom-rating-item">
                            <strong className="symptom-number">{num + 1}.</strong>
                            <div className="journal-line" style={{ flex: 1, marginBottom: 0, position: 'relative' }}>
                                <input
                                    type="text"
                                    value={formData[`symptom${num + 1}`]}
                                    onChange={(e) => onChange(`symptom${num + 1}`, e.target.value)}
                                    placeholder=""
                                    style={{
                                        backgroundColor: 'transparent',
                                        paddingRight: formData[`symptom${num + 1}`] ? '30px' : '10px'
                                    }}
                                    disabled={isLocked}
                                />
                                {formData[`symptom${num + 1}`] && (
                                    <button
                                        type="button"
                                        onClick={() => onChange(`symptom${num + 1}`, '')}
                                        style={{
                                            position: 'absolute',
                                            right: '8px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            color: '#999',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            lineHeight: '1'
                                        }}
                                        title="Clear this symptom"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            <div className="rating-input-wrapper">
                                <select
                                    value={formData[`rating${num + 1}`]}
                                    onChange={(e) => onChange(`rating${num + 1}`, e.target.value)}
                                    className="rating-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '24.5px',
                                        background: 'transparent',
                                        border: 'none',
                                        borderBottom: '1px solid #d4c4a8',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                                <span className="rating-divider" style={{
                                    fontFamily: 'Vintage Moon',
                                    fontSize: '24.5px'
                                }}>/10</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <p className="journal-prompt" style={{ marginTop: '40px' }}>
                Click any additional symptoms below that you have been experiencing and give it a rating out of ten.
            </p>

            {/* Symptom Table */}
            <div className="symptom-table-container">
                <div className="symptom-table-grid">
                    {/* Header */}
                    <div className="symptom-table-header">
                        <strong>Symptom</strong>
                    </div>
                    <div className="symptom-table-header symptom-table-rating-header">
                        <strong>Rating out of 10</strong>
                    </div>
                    <div className="symptom-table-header symptom-table-header-right">
                        <strong>Symptom</strong>
                    </div>
                    <div className="symptom-table-header symptom-table-rating-header symptom-table-header-last">
                        <strong>Rating out of 10</strong>
                    </div>

                    {/* Rows */}
                    {symptomsList.map((symptom, index) => (
                        <React.Fragment key={symptom}>
                            <div className={`symptom-table-cell ${index % 2 === 0 ? 'symptom-cell-dark' : 'symptom-cell-light'}`}>
                                {symptom}
                            </div>
                            <div className={`symptom-table-cell symptom-rating-cell ${index % 2 === 0 ? 'rating-cell-light' : 'rating-cell-lighter'}`}>
                                <select
                                    value={formData[`${symptom}_rating`] || ''}
                                    className="symptom-rating-table-input"
                                    onChange={(e) => onChange(`${symptom}_rating`, e.target.value)}
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '24.5px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>

                            {/* Move to next pair */}
                            {index % 2 === 1 && index < symptomsList.length - 1 && <></>}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <p style={{ fontSize: '14px', color: '#3d3425', marginTop: '30px', lineHeight: '1.7', }}>
                Reminder: if you haven't received a medical explanation for your symptoms, especially, if they are severe, it's highly
                recommended that you speak to a qualified health professional.
            </p>

            <JournalNavigation
                prevPage={null}
                nextPage="/journal/reflection"
            />
        </>
    );
};

export default SelfAssessment;
